import React from 'react';
import { DIALOG_TYPE } from '../../../../constants';
import { Button, EnhancedAccordian } from '../../../../ui';
import { AddQuestionModal } from '../../../../ui/add_question_modal/add_question_modal';
import { AddIcon, PencilIcon, TrashIcon } from '../../../../ui/icons/icons';
import { Loader } from '../../../../ui/loader/loader';
import { Modal } from '../../../../ui/modal/modal';
import { PaginationController } from '../../../../ui/pagination_controller/pagination_controller';
import { Permission } from '../../../../utils/helpers/permissions';
import PermissionGate from '../../../../utils/hooks/PermissionGate';
import { SelectOptions } from '../../../../utils/types/react_select_types';
import { SelectedQuestionContent } from '../../../job_openings/create/AssessmentQuestions/Containers/Questions/Containers/Components';
import { useQuestionBank } from '../Hooks';
import { QuestionsFilter } from './QUestionsFilter';

export const Questions: React.FC = () => {
  const {
    skillList,
    levels,
    subSkillsStore,
    jobTitles,
    searchObject,
    setSearchObject,
    searchTerm,
    setSearchTerm,
    setAddModalOpen,
    searchResults,
    onEditSave,
    deleteQuestionFromJob,
    setSkillList,
    expandedIds,
    toggleExpand,
    disabledMap,
    setDisabled,
    handleChange,
    handleSelectChange,
    handleMultiSelectChange,
    onAddNewSkill,
    questionTypes,
    pageNumber,
    totalPages,
    totalQuestions,
    switchPages,
    addModalOpen,
    addNewQuestion,
    loading,
    addFileViaInput,
    clearMediaFile,
  } = useQuestionBank();

  return (
    <>
      <div className='mb-3 rounded-lg border border-slate-100 bg-white p-2 shadow-sm'>
        <div className='flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between'>
          <QuestionsFilter
              skillList={skillList}
              levelList={levels.levelList}
              subSkillList={subSkillsStore.subSkillsList}
              jobTitleList={jobTitles.jobTitlesList}
              searchObject={searchObject}
              setSearchObject={setSearchObject}
          />

          <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end'>
            <input
                className='h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-normal normal-case leading-6 shadow-sm outline-none placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 sm:w-64'
                type='text'
                placeholder='Search Questions'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className='flex items-center gap-2 sm:justify-end'>
              <PermissionGate requiredPermissions={[Permission.QUESTIONS_CREATE]}>
                <Button
                    onClick={() => setAddModalOpen(true)}
                    variant='primary'
                    label='Create New Question'
                    icon={<AddIcon />}
                    hasIcon
                    className='min-h-9 px-3 py-1.5 text-sm'
                />
              </PermissionGate>
              <PermissionGate requiredPermissions={[Permission.QUESTIONS_DELETE]}>
                <Button
                    onClick={() => {}}
                    variant='secondary'
                    dialogType={DIALOG_TYPE.ERROR}
                    label='Delete'
                    icon={<TrashIcon />}
                    hasIcon
                    className='min-h-9 px-3 py-1.5 text-sm'
                />
              </PermissionGate>
            </div>
          </div>
        </div>
      </div>

      <div className='space-y-2'>
        { searchResults.map((q) => {
          const selectedProps = {
            toggledQuestion: q,
            isDisabled: disabledMap[q.id] ?? true,
            setIsDisabled: (d: boolean) => setDisabled(q.id, d),
            editQuestion: onEditSave,
            handleChange,
            handleSelectChange,
            handleMultiSelectChange,
            onAddNewSkill,
            returnSelectedJobTitles: () =>
              (q.jobTitle ?? [])
                .map((id) =>
                  jobTitles.jobTitlesList.find((t) => t.value === id),
                )
                .filter((x): x is SelectOptions => Boolean(x))
                .map((opt) => ({ label: opt.label, value: opt.value })),
            addFileViaInput,
            clearMediaFile,
            toggleCollapsible: () => toggleExpand(q.id),
            skills: skillList,
            subSkills1: subSkillsStore.subSkillsList
              .filter((s) => s.parentSkillId === q.skillId)
              .map((s) => ({ label: s.subskillName, value: s.id })),
            subSkills2: subSkillsStore.subSkillsList
              .filter((s) => s.parentSkillId === q.subSkill1Id)
              .map((s) => ({ label: s.subskillName, value: s.id })),
            subSkills3: subSkillsStore.subSkillsList
              .filter((s) => s.parentSkillId === q.subSkill2Id)
              .map((s) => ({ label: s.subskillName, value: s.id })),
            levelList: levels.levelList,
            jobTitleList: jobTitles.jobTitlesList,
            questionTypes,
          };

          return (
            <EnhancedAccordian
                key={q.id}
                title={q.question}
                isExpanded={expandedIds.includes(q.id)}
                onToggle={() => toggleExpand(q.id)}
                rightIcons={[
                  <button
                      key='edit'
                      className='text-slate-500 hover:text-emerald-700'
                      onClick={(e) => {
                        e.stopPropagation();
                        setDisabled(q.id, false);
                        if (!expandedIds.includes(q.id)) toggleExpand(q.id);
                      }}
                  >
                    <PencilIcon />
                  </button>,
                  <button
                      key='delete'
                      data-variant='danger'
                      className='text-slate-500 hover:text-red-600'
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteQuestionFromJob(q.id);
                      }}
                  >
                    <TrashIcon />
                  </button>,
                ]}
            >
              <SelectedQuestionContent {...selectedProps} />
            </EnhancedAccordian>
          );
        }) }
      </div>

      <div className='mt-3 flex flex-col gap-2 rounded-lg border border-slate-100 bg-white p-2 shadow-sm sm:flex-row sm:items-center sm:justify-between'>
        <div className='text-xs font-semibold text-slate-600'>
          Total Questions:{ ' ' }
          <span className='text-slate-900'>{ totalQuestions }</span>
        </div>
        <PaginationController
            pageNumber={pageNumber}
            maxPageNumber={totalPages}
            navigateNextPage={() => switchPages(pageNumber + 1)}
            navigatePreviousPage={() => switchPages(pageNumber - 1)}
        />
        <label className='flex items-center justify-between gap-2 text-xs font-semibold text-slate-600 sm:justify-end'>
          Rows
          <input
              className='h-8 w-16 rounded-md border border-slate-200 px-2 text-sm font-medium text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100'
              name='questionPage'
              type='number'
              min={1}
              value={searchObject.questionPage}
              onChange={(e) =>
                setSearchObject({
                  ...searchObject,
                  [e.target.name]: parseInt(e.target.value, 10),
                })
              }
          />
        </label>
      </div>

      <Modal
          visible={addModalOpen}
          headerText='Create New Question'
          customClass='max-w-5xl'
          modalContent={
            <AddQuestionModal
                addNewQuestion={addNewQuestion}
                skillList={skillList}
                levelList={levels.levelList}
                jobTitleList={jobTitles.jobTitlesList}
                subSkillList={subSkillsStore.subSkillsList}
                setSkillList={setSkillList}
            />
          }
          toggle={() => setAddModalOpen(false)}
      />

      <Loader loading={loading} />
    </>
  );
};
